import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faTimes,
    faEllipsisV,
} from '@fortawesome/free-solid-svg-icons';
import { quickStartApi, getSeasonalMessage } from 'js/utils';
import languageTypes from 'js/enums/language-types.enum';
import Alert from 'js/components/alert/alert';
import Box from 'js/components/box/box';
import Button from 'js/components/button/button';
import Pill from 'js/components/pill/pill';
import TopicButton from 'js/components/button/topic-button';
import SectionTitle from 'js/components/section-title/section-title';
import Text from 'js/components/text/text';
import TextArea from 'js/components/textarea/textarea';
import RandomLoadingMessage from 'js/pages/context-detail/components/ai-context-builder-drawer/random-loading-message';
import TopicsLibraryDrawer from 'js/pages/quick-start/components/topics-library-drawer/topics-library-drawer';
import DropdownMenu from 'js/components/dropdown-menu/dropdown-menu';
import DropdownButton from 'js/components/dropdown-menu/button';
import DropdownDivider from 'js/components/dropdown-menu/divider';
import JumboRadio from 'js/components/jumbo-radio/jumbo-radio';
import PromptActive from 'media/images/build-types/prompt-active.svg';
import PromptInactive from 'media/images/build-types/prompt-inactive.svg';
import FileActive from 'media/images/build-types/file-active.svg';
import FileInactive from 'media/images/build-types/file-inactive.svg';
import FileInput from 'js/components/input/file-input';
import {
    validateFileSize,
    validateMimeTypes,
} from 'js/components/button/file-button';
import Modal from 'js/components/modal/modal';
import buildTypeOptions from 'js/enums/build-types.enum';
import Row from 'js/components/grid/row';
import Col from 'js/components/grid/column';
import IndicatorBar from 'js/components/indicator-bar/indicator-bar';
import { AI_WAITING_MESSAGES, SEASONAL_MESSAGES } from 'js/constants';
import steps from '../enums/steps.enum';
import styles from '../quick-start.module.scss';

const MAX_FILE_SIZE = 1 * 1024 * 1024;

const EmptyResult = ({ isVideo }) => (
    <Alert theme="empty" title="No Results Found">
        <p>
            We didn’t come up with anything. Try changing your prompt to
            generate new topics{!isVideo && ' and keywords'}.
        </p>
    </Alert>
);

const QueryError = () => (
    <Alert
        theme="danger"
        title="There was an unexpected error when querying the AI"
    >
        <p>
            Hopefully it is only a temporary issue. Please try again in a few
            moments.
        </p>
    </Alert>
);

const KeywordPill = ({ active, children, onClick }) => (
    <Pill
        outline={!active}
        theme={active ? 'keyword' : 'muted'}
        suffixButton={
            onClick ? (
                <FontAwesomeIcon icon={active ? faTimes : faPlus} />
            ) : null
        }
        onSuffixClick={onClick}
    >
        {children}
    </Pill>
);

const PromptInput = ({ prompt, onPromptChange, onQuery }) => (
    <div className={styles['prompt-container']}>
        <TextArea
            focusOnShow
            rows={3}
            placeholder={getSeasonalMessage(SEASONAL_MESSAGES)}
            onChange={(ev) => onPromptChange(ev.target.value)}
            onClear={() => onPromptChange('')}
            value={prompt}
        />
        <div className={styles.button}>
            <Button theme="primary" disabled={!prompt.trim()} onClick={onQuery}>
                Generate
            </Button>
        </div>
    </div>
);

const BriefFileInput = ({ setLoading, onLoadEnd }) => {
    const [validationError, setValidationError] = useState('');
    const onLoadStart = () => {
        setLoading(true);
        setValidationError(null);
    };

    return (
        <FileInput
            onLoadStart={onLoadStart}
            onLoadEnd={onLoadEnd}
            sanitizers={[
                validateFileSize(1, MAX_FILE_SIZE),
                validateMimeTypes([
                    'application/pdf',
                    'application/vnd.ms-excel', // .xls
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
                    'application/msword', // .doc
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
                ]),
            ]}
            onError={setValidationError}
            hasError={!!validationError}
            errorMessage={
                <Text color={['red', 'base']}>{validationError}</Text>
            }
        />
    );
};

const ScaleWarningModal = ({ scaleScore, onContinue, onClose }) => (
    <Modal
        title="Great job but..."
        actionItems={
            <Row>
                <Col span="auto">
                    <Button onClick={onContinue} theme="outline">
                        Continue Anyway
                    </Button>
                </Col>
                <Col span="auto">
                    <Button onClick={onClose}>Let’s Improve My Scale</Button>
                </Col>
            </Row>
        }
        preventClosing
    >
        <Box margin={['smallest', 0, 'base', 0]}>
            <Row>
                <Col span={12} spanSm={5}>
                    <IndicatorBar
                        value={scaleScore}
                        lowMessage="Warning: Low Scale"
                        mediumMessage="Medium Scale"
                        highMessage="High Scale"
                        unknownMessage="No Scale"
                    />
                </Col>
            </Row>
        </Box>
        <Text color={['gray', 'dark']}>
            ...we recommend your context have at least a medium scale. You could
            try another prompt, or add topics and keywords to increase your
            scale.
        </Text>
    </Modal>
);

const QueryResults = ({ form, topics, keywords, onFormChange, allTopics }) => {
    const [isTopicsDropdownOpen, setIsTopicsDropdownOpen] = useState(false);
    const [isKeywordsDropdownOpen, setIsKeywordsDropdownOpen] = useState(false);
    const [isTopicsLibraryVisible, setTopicsLibraryVisible] = useState(false);
    const formTopicIds = (form.topics || []).map(({ id }) => id);
    const addTopics = (newTopics) => {
        onFormChange({
            ...form,
            topics: Array.from(new Set([...form.topics, ...newTopics])),
        });
    };
    const removeTopic = (topic) => {
        onFormChange({
            ...form,
            topics: form.topics.filter(({ id }) => id !== topic.id),
        });
    };
    const addKeywords = (newKeywords) => {
        onFormChange({
            ...form,
            keywords: Array.from(new Set([...form.keywords, ...newKeywords])),
        });
    };
    const removeKeyword = (keyword) => {
        onFormChange({
            ...form,
            keywords: form.keywords.filter((item) => item !== keyword),
        });
    };

    if (!topics.length && !keywords.length) {
        return <EmptyResult isVideo={form.is_video} />;
    }

    const toggleTopicsLibraryVisible = () => {
        setTopicsLibraryVisible((prev) => !prev);
    };

    const handleTopicsChange = (updatedTopics) => {
        onFormChange({
            ...form,
            topics: updatedTopics,
        });
    };
    const nonAppTopics = allTopics.filter((topic) => topic.is_app === false);
    const topicsToShow = [
        ...form.topics,
        ...topics.filter((t) => !form.topics.some((ft) => ft.id === t.id)),
    ];
    const allTopicsAdded = topics.every((t) => formTopicIds.includes(t.id));
    const allKeywordsAdded = keywords.every((k) => form.keywords.includes(k));

    return (
        <>
            <Box margin={['large', 0]}>
                {topics.length > 0 && (
                    <SectionTitle
                        hasBar
                        extension={[
                            <DropdownMenu
                                key="topics-menu"
                                width="fluid"
                                placement="bottom-end"
                                showMenu={isTopicsDropdownOpen}
                                onHide={() => setIsTopicsDropdownOpen(false)}
                                content={
                                    <ul>
                                        <DropdownButton
                                            disabled={allTopicsAdded}
                                            onClick={() => {
                                                if (!allTopicsAdded) {
                                                    addTopics(topics);
                                                    setIsTopicsDropdownOpen(
                                                        false,
                                                    );
                                                }
                                            }}
                                        >
                                            Add All Topics
                                        </DropdownButton>
                                        <DropdownDivider />
                                        <DropdownButton
                                            onClick={() => {
                                                toggleTopicsLibraryVisible();
                                            }}
                                        >
                                            View Topic Library
                                        </DropdownButton>
                                    </ul>
                                }
                            >
                                <Button
                                    size="small"
                                    square
                                    theme="outline"
                                    onClick={() =>
                                        setIsTopicsDropdownOpen(
                                            !isTopicsDropdownOpen,
                                        )
                                    }
                                    active={isTopicsDropdownOpen}
                                >
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                </Button>
                            </DropdownMenu>,
                        ]}
                    >
                        Topics
                    </SectionTitle>
                )}
                <Box margin={['small', 0, 0]}>
                    {topicsToShow.map((topic) => (
                        <Box
                            key={topic.id}
                            margin={['small', 'small', 0, 0]}
                            display="inline-block"
                        >
                            <TopicButton
                                logo={topic.group_logo}
                                action={
                                    formTopicIds.includes(topic.id)
                                        ? 'remove'
                                        : 'add'
                                }
                                theme={
                                    formTopicIds.includes(topic.id)
                                        ? 'targeted'
                                        : 'default'
                                }
                                onClick={
                                    formTopicIds.includes(topic.id)
                                        ? () => removeTopic(topic)
                                        : () => addTopics([topic])
                                }
                            >
                                {topic.name}
                            </TopicButton>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box margin={['large', 0]}>
                {keywords.length > 0 && (
                    <SectionTitle
                        hasBar
                        extension={[
                            <DropdownMenu
                                key="keywords-menu"
                                width="fluid"
                                placement="bottom-end"
                                showMenu={isKeywordsDropdownOpen}
                                onHide={() => setIsKeywordsDropdownOpen(false)}
                                content={
                                    <ul>
                                        <DropdownButton
                                            disabled={allKeywordsAdded}
                                            onClick={() => {
                                                if (!allKeywordsAdded) {
                                                    addKeywords(keywords);
                                                    setIsKeywordsDropdownOpen(
                                                        false,
                                                    );
                                                }
                                            }}
                                        >
                                            Add All Keywords
                                        </DropdownButton>
                                    </ul>
                                }
                            >
                                <Button
                                    size="small"
                                    square
                                    theme="outline"
                                    onClick={() =>
                                        setIsKeywordsDropdownOpen(
                                            !isKeywordsDropdownOpen,
                                        )
                                    }
                                    active={isKeywordsDropdownOpen}
                                >
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                </Button>
                            </DropdownMenu>,
                        ]}
                    >
                        Keywords
                    </SectionTitle>
                )}
                <Box margin={['small', 0, 0]}>
                    {keywords.map((keyword) => (
                        <Box
                            key={keyword}
                            margin={['small', 'small', 0, 0]}
                            display="inline-block"
                        >
                            <KeywordPill
                                active={form.keywords.includes(keyword)}
                                onClick={
                                    form.keywords.includes(keyword)
                                        ? () => removeKeyword(keyword)
                                        : () => addKeywords([keyword])
                                }
                            >
                                {keyword}
                            </KeywordPill>
                        </Box>
                    ))}
                </Box>
            </Box>
            {isTopicsLibraryVisible && (
                <TopicsLibraryDrawer
                    topics={nonAppTopics}
                    form={form}
                    onChange={handleTopicsChange}
                    onClose={toggleTopicsLibraryVisible}
                />
            )}
        </>
    );
};
const BuildContextStep = ({
    form,
    topics: allTopics,
    scaleScore,
    scaleError,
    onFormChange,
    onStepChange,
}) => {
    const [keywords, setKeywords] = useState(null);
    const [topics, setTopics] = useState(null);
    const [buildType, setBuildType] = useState(buildTypeOptions.PROMPT);
    const [prompt, setPrompt] = useState('');
    const [briefFile, setBriefFile] = useState(null);
    const [querying, setQuerying] = useState(false);
    const [queryingError, setQueryingError] = useState('');
    const [showScaleWarning, setShowScaleWarning] = useState(false);

    const executeQuery = async () => {
        setKeywords(null);
        setTopics(null);
        setQuerying(true);
        setQueryingError('');
        try {
            const response = await quickStartApi().query({
                user_data: prompt,
                brief_file: briefFile,
                language: form.is_video ? languageTypes.EN : form.language,
                is_video: form.is_video,
                is_app: form.is_app,
            });

            setKeywords(response.keywords);
            setTopics(
                response.topics
                    .map((topicId) =>
                        allTopics.find(({ id }) => id === topicId),
                    )
                    .filter((item) => item), // excludes brand-safety topics
            );
            onFormChange({
                ...form,
                queries: [
                    ...form.queries,
                    briefFile ? 'User uploaded a brief file.' : prompt,
                ],
            });
        } catch (err) {
            // if (err instanceof AbortError) return;
            setQueryingError(err);
        } finally {
            setQuerying(false);
        }
    };

    const onClickContinue =
        scaleScore >= 4 || !!scaleError
            ? () => onStepChange(steps.CHOOSE_DSP)
            : () => setShowScaleWarning(true);

    useEffect(() => {
        setPrompt('');
        setBriefFile(null);
        setTopics(null);
        setKeywords(null);
    }, [buildType]);

    useEffect(() => {
        if (briefFile) {
            executeQuery();
        }
    }, [briefFile]);

    return (
        <>
            <SectionTitle>
                Build out your context with the power of generative AI
            </SectionTitle>

            <Box margin={['base', 0, 0, 0]}>
                <Box margin={[0, 0, 'small', 0]}>
                    <Text size="large" weight="bold">
                        Build Type
                    </Text>
                </Box>
                <JumboRadio
                    options={[
                        {
                            label: 'Enter a prompt',
                            value: buildTypeOptions.PROMPT,
                            icons: [PromptActive, PromptInactive],
                        },
                        {
                            label: 'Upload a brief',
                            value: buildTypeOptions.BRIEF_FILE,
                            icons: [FileActive, FileInactive],
                        },
                    ]}
                    selectedValue={buildType}
                    onChange={setBuildType}
                />
            </Box>

            <Box margin={['base', 0, 0]}>
                <Text size="base" color={['gray', 'dark']}>
                    {buildType === buildTypeOptions.PROMPT
                        ? 'Describe the content you want to target, and we’ll suggest relevant topics and keywords. Generate unlimited descriptions and we’ll combine everything for you.'
                        : 'Upload a brief (PDF, Excel, or Word) with a max size of 1MB and we’ll suggest relevant topics and keywords. Upload unlimited briefs and we’ll combine everything for you.'}{' '}
                    <Text
                        size="base"
                        color={['gray', 'dark']}
                        weight="bold"
                        inline
                    >
                        Brand unsafe content is automatically filtered out.
                    </Text>
                </Text>
            </Box>

            <Box margin={['base', 0, 0]}>
                {querying && (
                    <Box margin={['large', 0]}>
                        <RandomLoadingMessage messages={AI_WAITING_MESSAGES} />
                    </Box>
                )}
                {!querying &&
                    (buildType === buildTypeOptions.PROMPT ? (
                        <PromptInput
                            prompt={prompt}
                            querying={querying}
                            onPromptChange={setPrompt}
                            onQuery={() => executeQuery()}
                        />
                    ) : (
                        <BriefFileInput
                            setLoading={() => setQuerying(true)}
                            onLoadEnd={setBriefFile}
                        />
                    ))}
            </Box>

            <Box margin={['large', 0, 0]}>
                {queryingError ? (
                    <QueryError />
                ) : (
                    topics !== null &&
                    keywords !== null && (
                        <QueryResults
                            form={form}
                            topics={topics}
                            keywords={keywords}
                            onFormChange={onFormChange}
                            allTopics={allTopics}
                        />
                    )
                )}
            </Box>

            <Box margin={['large', 0, 0]}>
                <div className={styles['wizard-buttons']}>
                    <Button
                        theme="outline"
                        onClick={() => onStepChange(steps.DETAILS)}
                    >
                        Previous
                    </Button>
                    <Button
                        disabled={!form.topics.length && !form.keywords.length}
                        onClick={onClickContinue}
                    >
                        Continue
                    </Button>
                </div>
            </Box>
            {showScaleWarning && (
                <ScaleWarningModal
                    scaleScore={scaleScore}
                    onContinue={() => onStepChange(steps.CHOOSE_DSP)}
                    onClose={() => setShowScaleWarning(false)}
                />
            )}
        </>
    );
};

export default BuildContextStep;
